import { sortBy } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import type { KdsLine, KdsLinesMap } from '../../redux/tickets/types/kds-line';
import type { LineCustomization } from '../../redux/tickets/types/kds-line/customization';
import type { KdsLineItem, KdsLineItemsMap } from '../../redux/tickets/types/kds-line-item';
import { getMapPath } from './getMapPath';

export type GroupedKdsLine = KdsLine & {
    sourceLinePaths: string[];
    groupKey: string;
};

function serializeCustomizations(customizations: LineCustomization[]): string {
    return [...customizations]
        .sort((a, b) => `${a.value}${a.quantity}`.localeCompare(`${b.value}${b.quantity}`))
        .map((c) => `${c.value}:${c.quantity}`)
        .join('|');
}

function buildGroupingKey(line: KdsLine): string {
    // Use name instead of productId because Web and POS channels assign different
    // internal productId values to the same product when no SKU is configured.
    return [
        line.name,
        line.comment ?? '',
        serializeCustomizations(line.customizations ?? []),
        line.status,
        line.measuredQuantity?.toString() ?? '',
        line.measurementUnit ?? '',
    ].join('|||');
}

// --- For SimpleTicket ---

function collectLeafLines(kdsLines: KdsLinesMap, parentPath?: string): Array<KdsLine & { linePath: string }> {
    const leaves: Array<KdsLine & { linePath: string }> = [];

    sortBy(Object.values(kdsLines), 'index').forEach((line) => {
        const linePath = getMapPath({ kdsLineUuid: line.uuid, parentPath });
        const hasChildren = Object.keys(line.kdsLines ?? {}).length > 0;

        if (!hasChildren) {
            leaves.push({ ...line, linePath });
        } else {
            leaves.push(...collectLeafLines(line.kdsLines, linePath));
        }
    });

    return leaves;
}

export function flattenAndGroupTicketLines(kdsLines: KdsLinesMap): GroupedKdsLine[] {
    const leaves = collectLeafLines(kdsLines);
    const groups: Record<string, GroupedKdsLine> = {};

    leaves.forEach((leaf) => {
        const key = buildGroupingKey(leaf);

        if (!groups[key]) {
            groups[key] = {
                ...leaf,
                kdsLines: {},
                sourceLinePaths: [leaf.linePath],
                groupKey: key,
            };
        } else {
            groups[key].quantity += leaf.quantity;
            groups[key].sourceLinePaths.push(leaf.linePath);
        }
    });

    return Object.values(groups);
}

// --- For GroupedTicket viewmodel ---

function stripMenuPrefix(name: string): string {
    return name.replace(/^\(M\) /, '');
}

function collectLeafLineItems(kdsLines: KdsLineItemsMap, parentItem: KdsLineItem): KdsLineItem[] {
    const leaves: KdsLineItem[] = [];

    sortBy(Object.values(kdsLines), 'index').forEach((line) => {
        const hasChildren = Object.keys(line.kdsLines ?? {}).length > 0;

        if (!hasChildren) {
            const normalized = { ...line, name: stripMenuPrefix(line.name) };
            leaves.push({
                ...normalized,
                groupingKey: buildGroupingKey(normalized),
                kdsLines: {},
                blockKey: parentItem.blockKey ?? line.blockKey,
            });
        } else {
            leaves.push(
                ...collectLeafLineItems(line.kdsLines, {
                    ...line,
                    blockKey: parentItem.blockKey ?? line.blockKey,
                }),
            );
        }
    });

    return leaves;
}

export function flattenLineItemsToLeaves(lineItems: KdsLineItem[]): KdsLineItem[] {
    // Identify product-menu names: items with children that are not pure formulas (no is_menu tag).
    // POS sends these as flat leaves with customizations; we need to extract those customizations
    // so the POS and Web representations can merge.
    const productMenuNames = new Set(
        lineItems
            .filter((item) => Object.keys(item.kdsLines ?? {}).length > 0 && !(item.tags ?? []).includes('is_menu'))
            .map((item) => stripMenuPrefix(item.name)),
    );

    const allLeaves: KdsLineItem[] = [];

    lineItems.forEach((item) => {
        const hasChildren = Object.keys(item.kdsLines ?? {}).length > 0;
        const normalizedName = stripMenuPrefix(item.name);

        if (!hasChildren) {
            if (productMenuNames.has(normalizedName) && (item.customizations ?? []).length > 0) {
                const strippedItem = { ...item, name: normalizedName, customizations: [] as LineCustomization[] };
                allLeaves.push({
                    ...strippedItem,
                    groupingKey: buildGroupingKey(strippedItem),
                    kdsLines: {},
                });
                (item.customizations ?? []).forEach((custo) => {
                    const custoAsItem = {
                        ...item,
                        uuid: uuidv4(),
                        name: stripMenuPrefix(custo.value),
                        quantity: custo.quantity,
                        customizations: [] as LineCustomization[],
                    };
                    allLeaves.push({
                        ...custoAsItem,
                        groupingKey: buildGroupingKey(custoAsItem),
                        kdsLines: {},
                    });
                });
            } else {
                const normalized = { ...item, name: normalizedName };
                allLeaves.push({
                    ...normalized,
                    groupingKey: buildGroupingKey(normalized),
                    kdsLines: {},
                });
            }
        } else {
            const isMenu = (item.tags ?? []).includes('is_menu');
            if (!isMenu) {
                const normalized = { ...item, name: normalizedName };
                allLeaves.push({
                    ...normalized,
                    groupingKey: buildGroupingKey(normalized),
                    kdsLines: {},
                });
            }
            allLeaves.push(...collectLeafLineItems(item.kdsLines, item));
        }
    });

    return allLeaves;
}
